import React from 'react';
import { errorService } from '@/lib/errorService';
import type { ContractDetail, ContractSearchResult } from '../types';

interface UseCustomerSelectionProps {
  /** The search results data containing contracts */
  data: ContractSearchResult | undefined;
  /** Callback when customer selection needs to be cleared (e.g., on search mode change) */
  onClear?: () => void;
}

interface UseCustomerSelectionReturn {
  /** Currently selected customer name */
  selectedCustomer: string;
  /** Array of unique customer names from search results */
  uniqueCustomers: string[];
  /** Whether customer selection UI should be shown (multiple customers exist) */
  shouldShowCustomerSelection: boolean;
  /** Filtered contracts based on selected customer */
  displayedContracts: ContractDetail[];
  /** Function to handle customer selection change */
  handleCustomerSelectionChange: (customerName: string) => void;
  /** Function to clear customer selection */
  clearCustomerSelection: () => void;
}

/**
 * Custom hook for managing customer selection logic in contract search.
 *
 * This hook encapsulates all customer-related state and logic including:
 * - Customer selection state management
 * - Filtering displayed contracts based on selected customer
 * - Calculating unique customers from search results
 *
 * Note: Auto-selection logic is handled at the component level to avoid
 * circular dependencies with contract selection state.
 *
 * @param props Configuration object for customer selection behavior
 * @returns Object containing customer selection state and handlers
 */
export function useCustomerSelection({
  data,
  onClear,
}: UseCustomerSelectionProps): UseCustomerSelectionReturn {
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>('');

  // Get unique customers from search results, using primaryCustomerKey to group by primary customer.
  // Multiple contracts with different customerFullName values (e.g., "Jane Smith" vs "Jane Smith/John Smith"
  // for co-buyer scenarios) will be grouped under the same primary customer when they share a primaryCustomerKey.
  // Display customerFullName for user clarity, even though grouping uses the normalized key.
  const uniqueCustomers = React.useMemo(() => {
    if (!data || data.status !== 'success') {
      return [];
    }

    // Create a map of primaryCustomerKey -> customerFullName to get unique primary customers
    const customerMap = new Map<string, string>();
    const contractsWithoutKey: string[] = [];

    data.contracts.forEach((contract) => {
      // Validate that primaryCustomerKey exists
      if (!contract.primaryCustomerKey) {
        contractsWithoutKey.push(contract.contractNumber);
        return; // Skip contracts without primaryCustomerKey
      }

      if (!customerMap.has(contract.primaryCustomerKey)) {
        customerMap.set(contract.primaryCustomerKey, contract.customerFullName);
      }
    });

    // Report data quality issue if contracts are missing primaryCustomerKey
    if (contractsWithoutKey.length > 0) {
      errorService.reportError(
        new Error('Contracts missing primaryCustomerKey'),
        {
          component: 'useCustomerSelection',
          contractCount: data.contracts.length,
          missingKeyCount: contractsWithoutKey.length,
          contractNumbers: contractsWithoutKey,
        }
      );
    }

    // Return the customerFullName values, sorted
    return Array.from(customerMap.values()).sort();
  }, [data]);

  // Determine if customer selection should be shown
  const shouldShowCustomerSelection = uniqueCustomers.length > 1;

  // Filter displayed contracts based on selected customer
  const displayedContracts = React.useMemo(() => {
    if (!data || data.status !== 'success') {
      return [];
    }

    if (!shouldShowCustomerSelection) {
      // Single customer or no customer selection needed - show all contracts
      return data.contracts;
    }

    if (!selectedCustomer) {
      // Multiple customers but none selected - show all contracts
      return data.contracts;
    }

    // Find the primaryCustomerKey for the selected customerFullName
    const selectedPrimaryCustomerKey = data.contracts.find(
      (contract) => contract.customerFullName === selectedCustomer
    )?.primaryCustomerKey;

    if (!selectedPrimaryCustomerKey) {
      // This indicates a data inconsistency - the selected customer should always be in the contracts list
      // since it was derived from uniqueCustomers. This could indicate a race condition or data mutation.
      errorService.reportError(
        new Error('Selected customer not found in contracts'),
        {
          component: 'useCustomerSelection',
          selectedCustomer,
          availableCustomers: Array.from(
            new Set(data.contracts.map((c) => c.customerFullName))
          ),
          contractCount: data.contracts.length,
        }
      );

      // Return empty array to indicate failure - don't show incorrect data
      return [];
    }

    // Filter contracts by selected customer's primary customer key
    return data.contracts.filter(
      (contract) => contract.primaryCustomerKey === selectedPrimaryCustomerKey
    );
  }, [data, selectedCustomer, shouldShowCustomerSelection]);

  const handleCustomerSelectionChange = React.useCallback(
    (customerName: string) => {
      setSelectedCustomer(customerName);
    },
    []
  );

  const clearCustomerSelection = React.useCallback(() => {
    setSelectedCustomer('');
    onClear?.();
  }, [onClear]);

  // Clear customer selection when data changes (new search)
  React.useEffect(() => {
    clearCustomerSelection();
  }, [data, clearCustomerSelection]);

  return {
    selectedCustomer,
    uniqueCustomers,
    shouldShowCustomerSelection,
    displayedContracts,
    handleCustomerSelectionChange,
    clearCustomerSelection,
  };
}
